import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PZeroLogo from "@/components/PZeroLogo";
import TourPreview from "@/components/TourPreview";

function useCursorBlur() {
  const [pos, setPos] = useState(null); // null until first move
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    const touch = window.matchMedia("(hover: none)").matches;
    setIsTouch(touch);
    if (touch) return;
    const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  return { pos, isTouch };
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { pos, isTouch } = useCursorBlur();
  const cardRef = useRef(null);
  const [cardCenter, setCardCenter] = useState(null);

  useEffect(() => {
    if (!isTouch) return;
    // On touch devices, park the blur behind the sign-in card.
    const measure = () => {
      const rect = cardRef.current?.getBoundingClientRect();
      if (rect) setCardCenter({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isTouch]);

  if (user && user !== false) {
    return <Navigate to={user.onboarding_complete ? "/" : "/onboarding"} replace />;
  }

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await endpoints.auth.forgotPassword(form.email);
        toast.success("If the email exists, a reset link was sent.");
        setMode("signin");
      } else if (mode === "signup") {
        const u = await endpoints.auth.register(form.name, form.email, form.password);
        setUser(u);
      } else {
        const u = await endpoints.auth.login(form.email, form.password);
        setUser(u);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { borderColor: "#E5E5E5", color: "#0A0A0A", background: "#FFFFFF" };

  // Cursor-following red blur — parks behind the login card on touch devices.
  const blurPos = isTouch ? cardCenter : pos;
  const blurStyle = blurPos
    ? { left: `${blurPos.x}px`, top: `${blurPos.y}px`, transform: "translate(-50%, -50%)" }
    : { left: "75%", top: "50%", transform: "translate(-50%, -50%)" };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden" style={{ background: "#FAFAFA", color: "#0A0A0A" }}>
      {/* Cursor-following red blur (touch: behind the card) */}
      <div
        aria-hidden
        data-testid="cursor-blur"
        className="fixed pointer-events-none z-0 h-[820px] w-[820px] rounded-full blur-3xl transition-[left,top] duration-500 ease-out"
        style={{
          ...blurStyle,
          background: "radial-gradient(circle, rgba(206,12,15,0.25) 0%, rgba(206,12,15,0.07) 45%, rgba(206,12,15,0) 72%)",
          opacity: 0.81,
        }}
      />

      {/* LEFT — 60-second tour teaser (hidden on mobile) */}
      <div className="hidden lg:flex flex-col justify-center px-10 xl:px-16 py-10 border-r border-neutral-200 relative z-10">
        <TourPreview />
        <p className="mt-8 text-[11px] font-mono text-neutral-500">
          Halcyon Labs
        </p>
      </div>

      {/* RIGHT — Sign-in card */}
      <div className="flex items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-sm" ref={cardRef}>
          <div className="fos-card p-6">
          <div className="flex items-center mb-5">
            <PZeroLogo height={42} variant="onLight" />
          </div>
          <h1 className="text-lg font-bold tracking-tight mb-1">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset password" : "Welcome back"}
          </h1>
          <p className="text-xs mb-5" style={{ color: "#737373" }}>
            {mode === "forgot"
              ? "Enter your email and we'll send a reset link."
              : "Built for the people every company runs on."}
          </p>

          {mode !== "forgot" && (
            <>
              <a
                href="/tour"
                data-testid="login-watch-tour"
                className="lg:hidden w-full mb-3 flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-md font-medium transition-colors hover:bg-blue-100"
                style={{ background: "#EFF6FF", color: "#1D4ED8" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                Watch the quick demo tour
              </a>
              <button
                onClick={googleLogin}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-md border font-medium transition-colors hover:bg-white/5"
                style={{ borderColor: "#E5E5E5" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px" style={{ background: "#E5E5E5" }} />
                <span className="text-[10px] mono" style={{ color: "#A3A3A3" }}>OR</span>
                <div className="flex-1 h-px" style={{ background: "#E5E5E5" }} />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input
                data-testid="signup-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                required
                className="w-full text-sm px-3 py-2.5 rounded-md border outline-none"
                style={inputStyle}
              />
            )}
            <input
              data-testid="login-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              required
              className="w-full text-sm px-3 py-2.5 rounded-md border outline-none"
              style={inputStyle}
            />
            {mode !== "forgot" && (
              <input
                data-testid="login-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                required
                minLength={6}
                className="w-full text-sm px-3 py-2.5 rounded-md border outline-none"
                style={inputStyle}
              />
            )}
            {error && (
              <p data-testid="auth-error" className="text-xs" style={{ color: "#F87171" }}>{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-form-submit-button"
              className="w-full text-sm px-3 py-2.5 rounded-md font-medium disabled:opacity-60"
              style={{ background: "#0A0A0A", color: "#FFFFFF" }}
            >
              {loading ? "Please wait…" : mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>

            <div className="flex items-center justify-between mt-4 text-xs" style={{ color: "#737373" }}>
              {mode === "signin" ? (
                <>
                  <button onClick={() => { setMode("signup"); setError(""); }} className="hover:text-neutral-900" data-testid="switch-to-signup">
                    Create account
                  </button>
                  <button onClick={() => { setMode("forgot"); setError(""); }} className="hover:text-neutral-900" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </>
              ) : (
                <button onClick={() => { setMode("signin"); setError(""); }} className="hover:text-neutral-900" data-testid="switch-to-signin">
                  ← Back to sign in
                </button>
              )}
            </div>
          </div>

          <p className="text-[10px] mono text-center mt-6" style={{ color: "#A3A3A3" }}>
            Priority Zero · v1.0 · Powered by Claude
          </p>
        </div>
      </div>
    </div>
  );
}
